import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/OrganizationsService';

import {Organization} from '../types';

// FNP-948: records are React Query's job; selection stays in Redux (FNP-951).
// The sibling organization-accounts module was already on React Query and is
// the in-repo reference for this shape.
//
// This service returns listOrganizations.items (a bare array) - no connection
// unwrap needed, unlike accounts / account-applications.

export const organizationsKeys = {
    all: ['organizations'] as const,
    lists: () => [...organizationsKeys.all, 'list'] as const,
    list: (filter: object) => [...organizationsKeys.lists(), filter] as const,
    details: () => [...organizationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...organizationsKeys.details(), id] as const,
};

export const useOrganizationsQuery = (filter: { [k: string]: string } = {}) => {
    return useQuery({
        queryKey: organizationsKeys.list(filter),
        queryFn: async (): Promise<Organization[]> => {
            const records = await fetchRecords(filter);
            return records ?? [];
        },
    });
};

export const useOrganizationQuery = (organizationId?: string) => {
    return useQuery({
        queryKey: organizationsKeys.detail(organizationId ?? ''),
        queryFn: () => fetchOne(organizationId as string),
        enabled: !!organizationId,
    });
};

export const useCreateOrganizationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: Organization) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: organizationsKeys.lists()});
        },
    });
};

export const useUpdateOrganizationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: Organization }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: organizationsKeys.lists()});
            queryClient.invalidateQueries({queryKey: organizationsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteOrganizationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: organizationsKeys.lists()});
        },
    });
};
